import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "@/content";
import { Wordmark } from "@/components/Wordmark";
import { BookButton } from "@/components/PrimaryCTA";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  return (
    <>
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-ink-900/80 backdrop-blur-2xl border-b border-white/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="I•AMovement home">
          <Wordmark size="text-lg sm:text-xl" />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide ${isActive ? "text-cyan" : "text-ice/80 hover:text-white"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><BookButton small /></div>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
          >
            <Menu size={26} />
          </button>
        </div>
      </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[130] md:hidden overflow-y-auto"
            style={{ backgroundColor: "#0C2C68" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <div className="pointer-events-none absolute inset-0 mesh-top opacity-40" />
            <div className="relative flex items-center justify-between px-5 py-5">
              <Wordmark size="text-lg" />
              <button data-testid="mobile-menu-close" aria-label="Close menu" onClick={() => setOpen(false)} className="text-white p-2">
                <X size={28} />
              </button>
            </div>
            <nav className="relative flex flex-col gap-1 px-6 pt-8 pb-10">
              {[{ label: "Home", to: "/" }, ...NAV, { label: "Book", to: "/book" }, { label: "Contact", to: "/contact" }].map((n, i) => (
                <motion.div key={n.to}
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.02 * i, duration: 0.2, ease: [0.25, 1, 0.5, 1] }}>
                  <Link to={n.to} data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                    className="font-display text-3xl font-bold text-white/90 hover:text-cyan block py-2 transition-colors">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="label mb-3">Get in touch</p>
                <a href="mailto:info@iamovement.co.uk" className="text-ice/80 block">info@iamovement.co.uk</a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
